import React, { useState, useCallback, useMemo } from 'react';
import useToggle from '_hooks/useToggle';
import useUserRole from '_hooks/useUserRole';
import HeaderOverview from './components/HeaderOverview';
import FilterBar from './components/FilterBar';
import ModalInvite from './components/ModalInvite';
import ModalApprove from './components/ModalApprove';
import useMembersData from './hooks/useMembersData';
import useFilterBar from './hooks/useFilterBar';
import TableMembers from './components/TableMembers';
import useMemberActions from './hooks/useMemberActions';
import ModalMemberDetail from './components/ModalMemberDetail';
import { getViewerMember, canManageMembers } from './helpers/permissions';
import './style.scss';

export default function MembersV2() {
  const [isOpenInviteModal, toggleInviteModal] = useToggle(false);
  const [isOpenModalMember, toggleModalMember] = useToggle(false);
  const [isOpenModalApprove, toggleModalApprove] = useToggle(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [pendingMember, setPendingMember] = useState(null);
  const {
    members,
    stats,
    loading,
    groupId,
    groupInfo,
    groupName,
    updateMemberStatus,
    refetchMembers,
  } = useMembersData();

  const { userInfo, isCurrentUser, isGroupOwner, canModifyMember } = useUserRole(groupInfo);

  const viewerMember = useMemo(
    () => getViewerMember(members, userInfo),
    [members, userInfo]
  );

  const canManage = useMemo(
    () => canManageMembers(groupInfo, viewerMember, userInfo),
    [groupInfo, viewerMember, userInfo]
  );

  const currentMember = useMemo(() => {
    if (!selectedMemberId || !Array.isArray(members)) return null;
    return (
      members.find(
        (m) => String(m.uid || m.id || m.userId) === String(selectedMemberId)
      ) || null
    );
  }, [members, selectedMemberId]);

  const {
    handleWhatsApp,
    handleBan,
    handleRemove,
    handleChangeRole,
    handleAcceptMember,
    handleRejectMember,
    handleInviteMember,
  } = useMemberActions({
    groupId,
    groupInfo,
    groupName,
    userInfo,
    onMemberUpdated: updateMemberStatus,
    onMemberInvited: refetchMembers,
  });

  const {
    search,
    role,
    sort,
    handleSearchChange,
    handleRoleChange,
    handleSortChange,
    filteredMembers,
  } = useFilterBar(members);

  const handleRowClick = useCallback(
    (member) => {
      if (!canManage) return;
      setSelectedMemberId(member?.uid || member?.id || member?.userId);
      toggleModalMember(true);
    },
    [canManage, toggleModalMember]
  );

  const handleClickPending = useCallback(
    (member) => {
      if (!canManage) return;
      setPendingMember(member);
      toggleModalApprove(true);
    },
    [canManage, toggleModalApprove]
  );

  if (loading && (!members || members.length === 0)) return <div>Loading...</div>;

  return (
    <div className="members-v2">
      <HeaderOverview stats={stats} onInvite={toggleInviteModal} canManage={canManage} />
      <FilterBar
        search={search}
        onSearchChange={handleSearchChange}
        role={role}
        onRoleChange={handleRoleChange}
        sort={sort}
        onSortChange={handleSortChange}
      />
      <TableMembers
        data={filteredMembers}
        onRowClick={canManage ? handleRowClick : null}
        onWhatsApp={handleWhatsApp}
        onEdit={canManage ? handleRowClick : null}
        onBan={handleBan}
        checkIsCurrentUser={isCurrentUser}
        checkIsOwner={isGroupOwner}
        canModify={canModifyMember}
        canManage={canManage}
        onClickPending={canManage ? handleClickPending : null}
      />
      <ModalInvite
        isOpen={isOpenInviteModal}
        toggle={toggleInviteModal}
        onInvite={handleInviteMember}
        canManage={canManage}
      />
      {canManage && (
        <ModalMemberDetail
          isOpen={isOpenModalMember}
          toggle={toggleModalMember}
          member={currentMember}
          groupId={groupId}
          isOwner={isGroupOwner(currentMember)}
          canModify={canModifyMember(currentMember)}
          onWhatsApp={handleWhatsApp}
          onBan={handleBan}
          onRemove={handleRemove}
          onChangeRole={handleChangeRole}
        />
      )}
      <ModalApprove
        isOpen={isOpenModalApprove}
        toggle={toggleModalApprove}
        member={pendingMember}
        onAccept={handleAcceptMember}
        onReject={handleRejectMember}
      />
    </div>
  );
}
